import { useEffect, useRef, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { Bell, X } from 'lucide-react'
import { useAuthStore } from '../../store/authStore'
import { supabase } from '../../lib/supabase'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

type OpenSession = {
  id: string
  session_number: string | null
  opened_at: string
  warehouses: { name: string } | null
}

function playBeep() {
  try {
    const ctx = new AudioContext()
    const beep = () => {
      const osc = ctx.createOscillator()
      const gain = ctx.createGain()
      osc.type = 'sine'
      osc.frequency.value = 800
      gain.gain.value = 0.3
      osc.connect(gain)
      gain.connect(ctx.destination)
      osc.start()
      setTimeout(() => { osc.stop(); }, 250)
    }
    beep()
    setTimeout(beep, 400)
    setTimeout(beep, 800)
  } catch {
    // AudioContext unavailable — silent fail
  }
}

function isAfterClosingTime(settings: Record<string, unknown> | null): boolean {
  if (!settings) return false
  const closingTime = settings['closing_time'] as string | undefined
  if (!closingTime) return false

  const now = new Date()
  const [h, m] = closingTime.split(':').map(Number)
  const closing = new Date()
  closing.setHours(h, m, 0, 0)

  const day = ['sun','mon','tue','wed','thu','fri','sat'][now.getDay()]
  const workingDays = (settings['working_days'] as string[] | undefined) ?? ['mon','tue','wed','thu','fri','sat']
  if (!workingDays.includes(day)) return false

  return now > closing
}

export function RegisterNotificationBanner() {
  const { tenant } = useAuthStore()
  const tid = tenant?.id ?? ''
  const settings = tenant?.settings as Record<string, unknown> | null
  const [dismissed, setDismissed] = useState(false)
  const hasPlayedRef = useRef(false)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const today = new Date().toISOString().slice(0, 10)

  const { data: openSessions = [], refetch } = useQuery<OpenSession[]>({
    queryKey: ['open-pos-sessions-check', tid, today],
    enabled: !!tid,
    staleTime: 60_000,
    queryFn: async () => {
      const { data } = await db('pos_sessions')
        .select('id, session_number, opened_at, warehouses(name)')
        .eq('tenant_id', tid)
        .eq('status', 'open')
        .is('closed_at', null)
        .gte('opened_at', today + 'T00:00:00')
      return (data ?? []) as OpenSession[]
    },
  })

  const shouldAlert = isAfterClosingTime(settings) && openSessions.length > 0 && !dismissed

  useEffect(() => {
    if (!shouldAlert) {
      if (intervalRef.current) clearInterval(intervalRef.current)
      return
    }

    if (!hasPlayedRef.current) {
      hasPlayedRef.current = true
      playBeep()
    }

    intervalRef.current = setInterval(() => {
      refetch()
      if (shouldAlert) playBeep()
    }, 10 * 60 * 1000)

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
  }, [shouldAlert, refetch])

  if (!shouldAlert) return null

  const first = openSessions[0]
  const firstLabel = first?.warehouses?.name
    ? `${first.warehouses.name}${first.session_number ? ` (${first.session_number})` : ''}`
    : (first?.session_number ?? 'POS session')

  return (
    <div className="bg-red-600 text-white px-4 py-3 flex items-start gap-3">
      <Bell className="h-5 w-5 flex-shrink-0 mt-0.5 animate-pulse" />
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm">POS Session Not Closed</p>
        <p className="text-xs text-red-100 mt-0.5">
          {openSessions.length === 1
            ? `${firstLabel} has not been closed. Business hours have ended.`
            : `${openSessions.length} POS sessions have not been closed. Business hours have ended.`}
        </p>
      </div>
      <Link
        to="/pos/sessions"
        className="flex-shrink-0 bg-white text-red-600 text-xs font-semibold px-3 py-1.5 rounded-lg hover:bg-red-50"
      >
        Close Session →
      </Link>
      <button className="flex-shrink-0 p-1 hover:bg-red-700 rounded" onClick={() => setDismissed(true)}>
        <X className="h-4 w-4" />
      </button>
    </div>
  )
}
